import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Users, Shield, User, Key, Copy, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    role: 'mitarbeiter'
  });
  const [tempPassword, setTempPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const openCreateDialog = useCallback(() => {
    setEditingUser(null);
    setFormData({ name: '', email: '', username: '', password: '', role: 'mitarbeiter' });
    setDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      username: user.username || '',
      password: '',
      role: user.role
    });
    setDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const openResetPasswordDialog = useCallback((user) => {
    setUserToReset(user);
    const newPassword = generateRandomPassword();
    setGeneratedPassword(newPassword);
    setTempPassword(newPassword);
    setPasswordCopied(false);
    setResetPasswordDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleCloseResetDialog = useCallback(() => {
    setResetPasswordDialogOpen(false);
    setUserToReset(null);
    setTempPassword('');
    setGeneratedPassword('');
  }, []);

  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleEmailChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const handleUsernameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/\s/g, '') }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, password: e.target.value }));
  }, []);

  const handleRoleChange = useCallback((value) => {
    if (value) {
      setFormData(prev => ({ ...prev, role: value }));
    }
  }, []);

  const copyPassword = useCallback(() => {
    navigator.clipboard.writeText(tempPassword);
    setPasswordCopied(true);
    toast.success('Passwort kopiert');
    setTimeout(() => setPasswordCopied(false), 3000);
  }, [tempPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name und E-Mail sind erforderlich');
      return;
    }

    if (!editingUser && !formData.password.trim()) {
      toast.error('Passwort ist erforderlich');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          username: formData.username || null,
          role: formData.role
        };
        if (formData.password.trim()) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('Benutzer aktualisiert');
      } else {
        await axios.post(`${API}/users`, {
          ...formData,
          username: formData.username || null
        });
        toast.success('Benutzer erstellt');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      const message = error.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset || !tempPassword.trim()) return;

    if (tempPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/users/${userToReset.id}/reset-password`, {
        temporary_password: tempPassword
      });
      toast.success('Ersatzpasswort gesetzt. Benutzer muss beim nächsten Login ein neues Passwort wählen.');
      setResetPasswordDialogOpen(false);
      setUserToReset(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to reset password:', error);
      const message = error.response?.data?.detail || 'Passwort-Reset fehlgeschlagen';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Benutzer gelöscht');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      const message = error.response?.data?.detail || 'Löschen fehlgeschlagen';
      toast.error(message);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-16">
        <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Berechtigung</h3>
        <p className="text-slate-500">Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 heading-app">Benutzerverwaltung</h1>
          <p className="text-slate-500">Verwalten Sie Administratoren und Mitarbeiter</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-user-button">
          <Plus className="w-4 h-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {/* Users List */}
      <div className="grid gap-4">
        {users.map((user) => (
          <Card key={user.id} className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    user.role === 'admin' ? 'bg-primary/10' : 'bg-slate-100'
                  }`}>
                    {user.role === 'admin' ? (
                      <Shield className="w-6 h-6 text-primary" />
                    ) : (
                      <User className="w-6 h-6 text-slate-500" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{user.name}</h3>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                      </Badge>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline">Sie</Badge>
                      )}
                      {user.password_reset_required && (
                        <Badge variant="destructive" className="text-xs">Passwort-Reset ausstehend</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{user.email}</p>
                    {user.username && (
                      <p className="text-xs text-slate-400">@{user.username}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {user.id !== currentUser?.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openResetPasswordDialog(user)}
                      data-testid={`reset-password-${user.id}`}
                    >
                      <Key className="w-4 h-4 mr-1" />
                      Ersatzpasswort
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(user)}
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {user.id !== currentUser?.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => openDeleteDialog(user)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Roles Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rollen-Übersicht</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-primary" />
                <h4 className="font-semibold">Administrator</h4>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Alle Bilder verwalten</li>
                <li>• CMS bearbeiten</li>
                <li>• Benutzer verwalten</li>
                <li>• Ersatzpasswörter setzen</li>
                <li>• Einstellungen ändern</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-slate-500" />
                <h4 className="font-semibold">Mitarbeiter</h4>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Bilder verwalten</li>
                <li>• Kategorien setzen</li>
                <li>• Downloads durchführen</li>
                <li>• Dashboard ansehen</li>
                <li className="text-slate-400">• Keine CMS-Bearbeitung</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen} modal={true}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Ändern Sie die Benutzerinformationen.'
                : 'Erstellen Sie einen neuen Benutzer für das System.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Name *</Label>
              <Input
                id="user-name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Max Mustermann"
                autoComplete="off"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-email">E-Mail *</Label>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                placeholder="benutzer@gb-sylt.de"
                disabled={!!editingUser}
                autoComplete="off"
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-username">Benutzername (optional)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">@</span>
                <Input
                  id="user-username"
                  value={formData.username}
                  onChange={handleUsernameChange}
                  placeholder="benutzername"
                  className="pl-8"
                  autoComplete="off"
                  data-testid="user-username-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Ermöglicht Login mit Benutzername statt E-Mail
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-password">
                Passwort {editingUser ? '(leer lassen für unverändert)' : '*'}
              </Label>
              <Input
                id="user-password"
                type="password"
                value={formData.password}
                onChange={handlePasswordChange}
                placeholder="••••••••"
                autoComplete="new-password"
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-role">Rolle *</Label>
              <Select
                value={formData.role}
                onValueChange={handleRoleChange}
              >
                <SelectTrigger id="user-role" data-testid="user-role-select">
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-button">
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen} modal={true}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Ersatzpasswort setzen</DialogTitle>
            <DialogDescription>
              Setzen Sie ein temporäres Passwort für {userToReset?.name}. 
              Der Benutzer muss nach dem Login ein neues Passwort wählen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Temporäres Passwort</Label>
              <div className="flex gap-2">
                <Input
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  data-testid="temp-password-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyPassword}
                  data-testid="copy-password-button"
                >
                  {passwordCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Generiert: {generatedPassword}
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Wichtig:</strong> Teilen Sie dem Benutzer das Passwort mit. 
                Nach dem Login wird er aufgefordert, ein neues Passwort zu setzen.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleCloseResetDialog}>
              Abbrechen
            </Button>
            <Button onClick={handleResetPassword} disabled={saving || !tempPassword.trim()}>
              {saving ? 'Wird gesetzt...' : 'Ersatzpasswort setzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Benutzer "{userToDelete?.name}" wird dauerhaft gelöscht.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDeleteDialog}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
